import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { IconButton, ListItem, ListItemText, List } from "@mui/material";
import { AppUser, User } from "./pages/api";
import {
  APP_LANGUAGE,
  APP_THEME,
  CURRENT_CLIENT,
  DEFAULT_LANGUAGE,
} from "./constants";
import { setAppUser, setDarkTheme, setLanguage } from "./store/appUserReducer";
import { useGetUserInDepthQuery } from "./pages/user/slice";
import { RootState } from "./store";
import cloneDeep from "lodash/cloneDeep";
import { toAddress } from "./utils/address";
import { App } from "./models";
import Iconify from "./components/Iconify";
import Modal from "./components/Modal";
import Loader from "./components/Loader";
import { toErrorMsg } from "./utils";
import { getCookie, setCookie } from "./utils/cookie";

interface IProps {
  setIsActiveClient: (isActiveClient: boolean) => void;
  setIsLoaded: (isLoaded: boolean) => void;
}
export default ({ setIsActiveClient, setIsLoaded }: IProps) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const appUser: AppUser = useSelector((state: RootState) => state.appUser);

  const userResult = useGetUserInDepthQuery();
  const [hasClientSelection, setHasClientSelection] = useState(false);

  useEffect(() => {
    let lang: string | null = getCookie(APP_LANGUAGE);
    if (!lang?.length && DEFAULT_LANGUAGE?.length > 0) {
      lang = DEFAULT_LANGUAGE;
    }
    if (lang?.length) {
      i18n
        .changeLanguage(lang)
        // Keep Redux in sync only after the async language resource is ready.
        // Components that memoize translated values by appUser.language then
        // recompute with the new i18next translation function.
        .then(() => dispatch(setLanguage(lang as string)))
        .catch((err) => console.log(err));
    }

    let appTheme: string | null = getCookie(APP_THEME);
    if (appTheme) {
      dispatch(setDarkTheme(appTheme === "dark"));
    }
  }, []);

  useEffect(() => {
    if (userResult?.isSuccess) {
      if (
        userResult?.data?.user &&
        userResult.data.user.isActive &&
        userResult.data.user.client?.isActive &&
        userResult.data.user.clients?.length > 0 &&
        userResult.data.clients?.length > 0
      ) {
        let ccId: string | null = getCookie(CURRENT_CLIENT);
        if (userResult.data.user.clients?.length === 1) {
          ccId = userResult.data.user.clients[0];
        }

        if (ccId) {
          const currentClient = userResult.data.clients.find(
            (c: any) => c.id === ccId,
          );
          if (currentClient && currentClient.id) {
            updateAppUser(currentClient);
          } else {
            // open popup
            setHasClientSelection(true);
          }
        } else {
          setHasClientSelection(true);
        }
      } else {
        removeAppUser();
      }
    } else if (userResult?.isError) {
      removeAppUser();
    }
  }, [userResult.isSuccess, userResult.isError, userResult.data]);

  const updateAppUser = (currentClient: any) => {
    // const currentClient = userResult.data.clients.find(
    //   (c: any) => c.id === ccId,
    // );
    // if (currentClient && currentClient.id) {
    setCookie(CURRENT_CLIENT, currentClient?.id);
    let updatedappUser = cloneDeep(appUser);
    if (updatedappUser) {
      updatedappUser.user = {
        ...userResult.data.user,
        client: {
          ...userResult.data.user.client,
          country: userResult.data.user.client?.address
            ? toAddress(userResult.data.user.client?.address)?.country
            : "",
        },
        currentClient: {
          ...currentClient,
          country: currentClient?.address
            ? toAddress(currentClient?.address)?.country
            : "",
        },
        clients: userResult.data.clients,
      } as User;
      dispatch(setAppUser(updatedappUser));

      if (process.env.REACT_APP_APP !== App.APP) {
        if (
          updatedappUser?.user?.client &&
          ((updatedappUser.user.client.isBranch &&
            (!updatedappUser.user.client.parent ||
              !updatedappUser.user.client.parent.subscription ||
              !updatedappUser.user.client.parent.subscription.activeTill ||
              (updatedappUser.user.client.parent.subscription.activeTill &&
                new Date(
                  updatedappUser.user.client.parent.subscription.activeTill,
                ).getTime() < new Date().getTime()))) ||
            (!updatedappUser.user.client.isBranch &&
              (!updatedappUser.user.client.subscription ||
                !updatedappUser.user.client.subscription.activeTill ||
                (updatedappUser.user.client.subscription.activeTill &&
                  new Date(
                    updatedappUser.user.client.subscription.activeTill,
                  ).getTime() < new Date().getTime()))))
        ) {
          setIsActiveClient(false);
        }
      }

      setIsLoaded(true);
      if (
        location.pathname === "/" ||
        location.pathname === "/login" ||
        location.pathname === "/register" ||
        location.pathname === "/fpass"
      ) {
        navigate("/", { replace: true });
      }
    } else {
      removeAppUser();
    }
    // } else {
    //   removeAppUser();
    // }
  };

  const removeAppUser = () => {
    setIsLoaded(true);
    if (appUser?.user?.client?.id) {
      dispatch(setAppUser(null as any));
      // if (window.location.href.indexOf("feedback/") < 0) {
      //   window.location.href = "/";
      // }
      navigate("/login");
    }
  };

  const renderBranch = (client: any) => {
    return client?.id ? (
      <ListItem
        key={client.id}
        disableGutters
        secondaryAction={
          <IconButton aria-label="GoToDashboard">
            <Iconify
              icon="ri:login-circle-line"
              width={30}
              sx={{ color: "success.darker" }}
            />
          </IconButton>
        }
        onClick={() => onSelectBranch(client)}
        title={t("GoToDashboard").toString()}
        sx={{
          cursor: "pointer",
          paddingLeft: "10px",
          "&:hover": {
            backgroundColor: "background.neutral",
          },
        }}
      >
        <ListItemText
          primary={
            <span style={client.isBranch ? {} : { fontWeight: "bold" }}>
              {!client.isBranch ? t("MainAdminDashboard") : client.name}
            </span>
          }
          secondary={client.tagLine}
        />
      </ListItem>
    ) : null;
  };

  const onSelectBranch = (client: any) => {
    updateAppUser(client);
  };

  return hasClientSelection ? (
    <Modal
      title={t("SelectOrgToLogin")}
      // onCancel={() => {
      //   setHasClientSelection(false);
      // }}
    >
      <List sx={{ width: "100%", bgcolor: "background.paper" }}>
        {userResult?.data?.clients
          .filter((c: any) => !c.isBranch)
          .map((c: any) => renderBranch(c))}
        {userResult?.data?.clients
          .filter((c: any) => c.isBranch)
          .map((c: any) => renderBranch(c))}
      </List>
    </Modal>
  ) : userResult?.isLoading ? (
    <Loader />
  ) : userResult.isError ? (
    <div>{toErrorMsg(userResult.error)}</div>
  ) : null;
};
